import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddRounded, DeleteOutlineRounded, GroupsRounded, SaveRounded } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TenantDateTimeField } from '../../../components/TenantDateTimeField';
import { TenantDateField } from '../../../components/TenantDateField';
import { useConfirm } from '../../../components/ConfirmProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { mentorApi, type MentorCadence, type MentorSessionDto } from '../../../lib/api/mentor.api';
import { PortalLoading } from '../PortalShell';
import { errorText } from '../../../lib/error-text';
import { formatDateTime } from '../../../lib/i18n-format';

export function MentorPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const qc = useQueryClient();

  const pairingQ = useQuery({
    queryKey: ['portal-v2-mentor-pairing', currentTenantId, personId],
    enabled: !!currentTenantId,
    queryFn: () =>
      mentorApi.getMyPairing({
        tenantId: currentTenantId!,
        ...(personId ? { studentId: personId } : {}),
      }),
  });
  const sessionsQ = useQuery({
    queryKey: ['portal-v2-mentor-sessions', currentTenantId, personId],
    enabled: !!currentTenantId,
    queryFn: () =>
      mentorApi.listMySessions({
        tenantId: currentTenantId!,
        ...(personId ? { studentId: personId } : {}),
      }),
  });

  const [mentorName, setMentorName] = useState('');
  const [mentorEmail, setMentorEmail] = useState('');
  const [cadence, setCadence] = useState<MentorCadence>('monthly');
  const [nextSlot, setNextSlot] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pairingQ.data) {
      setMentorName(pairingQ.data.mentorName ?? '');
      setMentorEmail(pairingQ.data.mentorEmail ?? '');
      setCadence(pairingQ.data.cadence);
      setNextSlot(pairingQ.data.nextSlot ?? '');
    }
  }, [pairingQ.data]);

  const saveProfile = async () => {
    if (!currentTenantId) return;
    setSavingProfile(true);
    setError(null);
    try {
      await mentorApi.updateMyPairing({
        tenantId: currentTenantId,
        ...(personId ? { studentId: personId } : {}),
        mentorName: mentorName || null,
        mentorEmail: mentorEmail || null,
        cadence,
        nextSlot: nextSlot || null,
      });
      qc.invalidateQueries({ queryKey: ['portal-v2-mentor-pairing'] });
    } catch (e) {
      setError(errorText(e, t('portal.mentor.saveError')));
    } finally {
      setSavingProfile(false);
    }
  };

  const addSession = async () => {
    if (!currentTenantId) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      await mentorApi.createMySession({
        tenantId: currentTenantId,
        ...(personId ? { studentId: personId } : {}),
        date: today,
        duration: '30 min',
        topic: '',
        notes: '',
      });
      qc.invalidateQueries({ queryKey: ['portal-v2-mentor-sessions'] });
    } catch (e) {
      setError(errorText(e, t('portal.mentor.createSessionError')));
    }
  };

  if (pairingQ.isLoading || sessionsQ.isLoading) return <PortalLoading />;

  const sessions = sessionsQ.data?.items ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#e6edf3">
          {t('portal.mentor.title')}
        </Typography>
        <Typography variant="body2" color="#8b949e">
          {t('portal.mentor.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Profile */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Avatar sx={{ bgcolor: '#6366f1', width: 48, height: 48 }}>
            <GroupsRounded />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="#8b949e">
              {t('portal.mentor.myMentor')}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
              {pairingQ.data?.mentorName || t('portal.mentor.unassigned')}
            </Typography>
            {pairingQ.data?.mentorEmail && (
              <Typography variant="caption" color="#8b949e">
                {pairingQ.data.mentorEmail}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label={t('portal.mentor.fieldMentorName')}
            value={mentorName}
            onChange={(e) => setMentorName(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label={t('portal.mentor.fieldMentorEmail')}
            value={mentorEmail}
            onChange={(e) => setMentorEmail(e.target.value)}
            type="email"
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              select
              label={t('portal.mentor.fieldCadence')}
              value={cadence}
              onChange={(e) => setCadence(e.target.value as MentorCadence)}
              sx={{ minWidth: { xs: 0, sm: 160 } }}
              fullWidth
            >
              <MenuItem value="weekly">{t('portal.mentor.cadenceWeekly')}</MenuItem>
              <MenuItem value="biweekly">{t('portal.mentor.cadenceBiweekly')}</MenuItem>
              <MenuItem value="monthly">{t('portal.mentor.cadenceMonthly')}</MenuItem>
              <MenuItem value="adhoc">{t('portal.mentor.cadenceAdhoc')}</MenuItem>
            </TextField>
            <TenantDateTimeField
              label={t('portal.mentor.fieldNextSlot')}
              value={nextSlot}
              onChange={setNextSlot}
              fullWidth
            />
          </Stack>
          {nextSlot && (
            <Alert severity="success">
              {t('portal.mentor.nextSessionLabel')} <strong>{formatDateTime(nextSlot)}</strong>
            </Alert>
          )}
          <Button
            variant="contained"
            startIcon={
              savingProfile ? <CircularProgress size={16} color="inherit" /> : <SaveRounded />
            }
            onClick={saveProfile}
            disabled={savingProfile}
            sx={{ alignSelf: 'flex-end' }}
          >
            {savingProfile ? t('portal.mentor.saving') : t('portal.mentor.saveProfile')}
          </Button>
        </Stack>
      </Card>

      {/* Sessions log */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Typography variant="h5" fontWeight={700} color="#e6edf3">
          {t('portal.mentor.sessionsLog')}
        </Typography>
        <Button startIcon={<AddRounded />} variant="contained" onClick={addSession}>
          {t('portal.mentor.newSession')}
        </Button>
      </Stack>

      {sessions.length === 0 && (
        <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 3 }}>
          <Typography variant="body2" color="#8b949e" textAlign="center">
            {t('portal.mentor.sessionsEmpty')}
          </Typography>
        </Card>
      )}

      {sessions.map((sess) => (
        <SessionRow key={sess.id} session={sess} />
      ))}
    </Stack>
  );
}

function SessionRow({ session }: { session: MentorSessionDto }): JSX.Element {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const qc = useQueryClient();
  const [date, setDate] = useState(session.date);
  const [duration, setDuration] = useState(session.duration);
  const [topic, setTopic] = useState(session.topic);
  const [notes, setNotes] = useState(session.notes);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    if (!currentTenantId) return;
    setSaving(true);
    try {
      await mentorApi.updateMySession({
        tenantId: currentTenantId,
        sessionId: session.id,
        patch: { date, duration, topic, notes },
      });
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['portal-v2-mentor-sessions'] });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!currentTenantId) return;
    if (
      !(await confirm({ message: t('portal.mentor.confirmDeleteSession'), confirmColor: 'error' }))
    )
      return;
    await mentorApi.deleteMySession({ tenantId: currentTenantId, sessionId: session.id });
    qc.invalidateQueries({ queryKey: ['portal-v2-mentor-sessions'] });
  };

  return (
    <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        mb={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <TenantDateField
            size="small"
            label={t('portal.mentor.fieldDate')}
            value={date}
            onChange={(v) => {
              setDate(v);
              setDirty(true);
            }}
          />
          <TextField
            size="small"
            label={t('portal.mentor.fieldDuration')}
            value={duration}
            onChange={(e) => {
              setDuration(e.target.value);
              setDirty(true);
            }}
            sx={{ width: { xs: '40%', sm: 120 } }}
          />
        </Stack>
        <TextField
          size="small"
          label={t('portal.mentor.fieldTopic')}
          value={topic}
          onChange={(e) => {
            setTopic(e.target.value);
            setDirty(true);
          }}
          sx={{ flex: 1 }}
          fullWidth
        />
        <Stack
          direction="row"
          spacing={1}
          justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}
          alignItems="center"
        >
          {dirty && (
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveRounded />}
              onClick={save}
              disabled={saving}
            >
              {t('portal.mentor.save')}
            </Button>
          )}
          <IconButton color="error" onClick={remove}>
            <DeleteOutlineRounded />
          </IconButton>
        </Stack>
      </Stack>
      <TextField
        size="small"
        label={t('portal.mentor.fieldNotes')}
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setDirty(true);
        }}
        multiline
        minRows={3}
        fullWidth
      />
    </Card>
  );
}
